import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, MessageSquare, TrendingUp, Clock } from 'lucide-react';
import { ageCategories } from '../data/mock';

const SeriesOverviewPage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-hero mb-4">Series Overview</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              One book per age. A clear 40-week sequence. Scripts and practice that grow with your child.
            </p>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1 section-title">What's Included in Every Book</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-blue-200)' }}>
                  <Calendar size={28} style={{ color: 'var(--brand-dark-blue)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-2">40-Week Curriculum</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                One year of structured learning with weekly skill targets and daily practice.
              </p>
            </div>

            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-orange-200)' }}>
                  <MessageSquare size={28} style={{ color: 'var(--brand-orange)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-2">Parent Scripts</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Exact language for conflict, repair, boundaries, and big feelings.
              </p>
            </div>

            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-green-200)' }}>
                  <TrendingUp size={28} style={{ color: 'var(--brand-green)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-2">Skill Progression</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Skills build week by week, creating a clear developmental path.
              </p>
            </div>

            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-pink-200)' }}>
                  <Clock size={28} style={{ color: 'var(--text-primary)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-2">10 Minutes Daily</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Short lessons designed for busy families.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Progression Map Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1 section-title">Skill Progression by Age</h2>
            <p className="body-large section-subtitle">
              Each stage builds on the previous one, creating a complete emotional development roadmap
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Foundations */}
            <div className="card hover-lift" style={{ background: 'var(--accent-purple-200)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <BookOpen size={24} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <h3 className="heading-3">Ages 4-7</h3>
                  <p className="caption">Foundations</p>
                </div>
              </div>
              <p className="body-medium mb-4">
                {ageCategories.foundations.description}
              </p>
              <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                <li>• Naming and recognizing feelings</li>
                <li>• Body cues and calm-down tools</li>
                <li>• Simple boundary phrases</li>
                <li>• Repair and apology basics</li>
              </ul>
            </div>

            {/* Communication */}
            <div className="card hover-lift" style={{ background: 'var(--accent-blue-200)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <MessageSquare size={24} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <h3 className="heading-3">Ages 8-12</h3>
                  <p className="caption">Communication & Conflict</p>
                </div>
              </div>
              <p className="body-medium mb-4">
                {ageCategories.communication.description}
              </p>
              <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                <li>• I-statements and active listening</li>
                <li>• Conflict negotiation skills</li>
                <li>• Friendship dynamics and inclusion</li>
                <li>• Handling unfairness and disappointment</li>
              </ul>
            </div>

            {/* Teen Skills */}
            <div className="card hover-lift" style={{ background: 'var(--accent-orange-200)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <TrendingUp size={24} style={{ color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <h3 className="heading-3">Ages 13-18</h3>
                  <p className="caption">Teen Skills & Leadership</p>
                </div>
              </div>
              <p className="body-medium mb-4">
                {ageCategories.teenSkills.description}
              </p>
              <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                <li>• Social pressure and consent</li>
                <li>• Self-respect and boundaries</li>
                <li>• Digital life and online communication</li>
                <li>• Leadership and emotional influence</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What a Week Looks Like */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-1 mb-4">What a Week Looks Like</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Each week focuses on one skill with daily practice.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-blue-200)' }}>
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="heading-3 mb-1">Monday: Introduction</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Learn the week's skill target and why it matters
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-orange-200)' }}>
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="heading-3 mb-1">Tuesday-Thursday: Practice</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Short daily activities with parent scripts and real-world application
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-green-200)' }}>
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="heading-3 mb-1">Friday: Regulation Tool</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Learn and practice a calming or coping strategy
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1713942590283-59867d5e3f8d" 
                alt="Parent and child reading together"
                className="rounded-lg shadow-lg w-full hover-lift"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Time Commitment */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto card text-center" style={{ background: 'var(--bg-card)' }}>
            <Clock size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-accent)' }} />
            <h2 className="heading-2 mb-4">Time Commitment</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Designed for busy families: 5-10 mins daily or 1 longer session per week. 
              The curriculum is flexible—go at your child's pace.
            </p>
            <p className="body-small" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
              "Ten minutes a day can change the next ten years."
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Ready to Start?</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Choose your child's age and begin building emotional skills that last a lifetime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-here" className="btn-primary">
                Find Your Child's Book
              </Link>
              <Link to="/resources" className="btn-secondary">
                Pre-Order Books Ages 5-18
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeriesOverviewPage;
